import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { findBlogPost, BUSINESS, type BlogPost } from "@/lib/site-data";
import { AnswerBlock } from "@/components/AnswerBlock";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = findBlogPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Post not found" }] };
    const p = loaderData.post;
    const title = `${p.title} | ${BUSINESS.shortName}`;
    return {
      meta: [
        { title },
        { name: "description", content: p.description },
        { property: "og:title", content: p.title },
        { property: "og:description", content: p.description },
        { property: "og:url", content: `/blog/${params.slug}` },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: p.publishedAt },
      ],
      links: [{ rel: "canonical", href: `https://lehitradessaeo.com/blog/${params.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: p.title,
            description: p.description,
            datePublished: p.publishedAt,
            author: { "@type": "Organization", name: BUSINESS.name },
            publisher: { "@type": "Organization", name: BUSINESS.name },
            mainEntityOfPage: `https://lehitradessaeo.com/blog/${params.slug}`,
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["[data-speakable=\"question\"]", "[data-speakable=\"answer\"]"],
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Post not found</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/blog" className="underline">Back to blog</Link>.
      </p>
    </main>
  ),
  component: BlogPostPage,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  return (
    <main className="mx-auto max-w-3xl px-6 py-20">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        {new Date(post.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })} · {post.readMinutes} min read
      </p>
      <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">{post.title}</h1>
      <p className="mt-5 text-lg text-muted-foreground">{post.excerpt}</p>

      <AnswerBlock
        question={`TL;DR: ${post.title}`}
        answer={post.description}
      />

      <div className="mt-12 space-y-10">
        {post.body.map((section: BlogPost["body"][number]) => (
          <section key={section.heading}>
            <h2 className="font-display text-2xl tracking-tight">{section.heading}</h2>
            {section.paragraphs.map((para: string, idx: number) => (
              <p key={idx} className="mt-4 leading-relaxed text-muted-foreground">{para}</p>
            ))}
            {section.bullets && (
              <ul className="mt-4 space-y-2">
                {section.bullets.map((b: string) => (
                  <li key={b} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    {b}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <div className="mt-16 rounded-2xl border border-border bg-card p-8 text-center">
        <h3 className="font-display text-2xl tracking-tight">Want this playbook run for your business?</h3>
        <p className="mt-3 text-muted-foreground">Free local SEO audit. No pitch deck, no contract pressure.</p>
        <Link
          to="/contact"
          className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Get my free audit
        </Link>
      </div>
    </main>
  );
}